/**
 * Unit Classification for Field Type Selection
 *
 * Leverages the same logic as UnitConversionService to determine
 * which units can be converted vs which are universal/scientific.
 * This creates a single source of truth for unit behavior.
 */

/**
 * Conversion factors for length units (base unit: m - SI standard)
 * Used by both unit classification and unit conversion
 */
export const lengthConversions: Record<string, number> = {
	mm: 0.001, // 1 mm = 0.001 m
	m: 1, // base unit - meters (SI)
	foot: 0.3048, // 1 foot = 0.3048 m
	inch: 0.0254, // 1 inch = 0.0254 m
	mAD: 1, // meters above datum (same as meters)
};

/**
 * Conversion factors for pressure units (base unit: mH2O - meters of water head)
 * Used by both unit classification and unit conversion
 */
export const pressureConversions: Record<string, number> = {
	mH2O: 1, // base unit - meters of water head
	mHead: 1, // alias for mH2O - meters of water head
	'm/head': 1, // alias for mH2O - meters per head
	psi: 0.70306958, // 1 psi = 0.70307 mH2O
};

/**
 * Conversion factors for flow units (base unit: m3/s)
 * Used by both unit classification and unit conversion
 */
export const flowConversions: Record<string, number> = {
	'm³/s': 1, // base unit (legacy unit)
	'm3/s': 1, // base unit
	'l/s': 0.001, // liters per second to cubic meters per second
	gpm: 0.00006309, // gallons per minute to cubic meters per second
	'ft3/s': 0.0283168, // cubic feet per second to cubic meters per second
};

/**
 * Conversion factors for volume units (base unit: m3)
 * Used by both unit classification and unit conversion
 */
export const volumeConversions: Record<string, number> = {
	m3: 1, // base unit
	ft3: 0.0283168, // cubic feet to cubic meters
};

/**
 * Conversion factors for velocity units (base unit: m/s)
 * Used by both unit classification and unit conversion
 */
export const velocityConversions: Record<string, number> = {
	'm/s': 1, // base unit
	'ft/s': 0.3048, // feet per second to meters per second
};

/**
 * Conversion factors for area units (base unit: m2)
 * Used by both unit classification and unit conversion
 */
export const areaConversions: Record<string, number> = {
	m2: 1, // base unit
	ft2: 0.092903, // square feet to square meters
};

/**
 * Check if a unit can be converted (has metric/imperial equivalents)
 * Uses the same logic as UnitConversionService.canUnitBeConverted()
 * @param unit - The unit string to check
 * @returns true if the unit has conversion capabilities
 */
const canUnitBeConverted = (unit: string | undefined): boolean => {
	if (!unit) {
		return false;
	}

	const normalizedUnit = unit.trim();

	// Check if the unit exists in any conversion category
	return (
		lengthConversions[normalizedUnit] !== undefined ||
		pressureConversions[normalizedUnit] !== undefined ||
		flowConversions[normalizedUnit] !== undefined ||
		volumeConversions[normalizedUnit] !== undefined ||
		velocityConversions[normalizedUnit] !== undefined ||
		areaConversions[normalizedUnit] !== undefined
	);
};

/**
 * Currency Units - handled by CurrencyField
 * These have special formatting requirements
 */
const CURRENCY_UNITS = ['$'];

export enum FieldType {
	SYSTEM_OF_MEASUREMENT = 'system-of-measurement', // Use UnitField
	SCIENTIFIC_UNIT = 'scientific-unit', // Use ScientificUnitField
	CURRENCY = 'currency', // Use CurrencyField
	UNKNOWN = 'unknown', // Fallback
}

/**
 * Classify a unit string to determine which field component to use
 * Uses the same logic as UnitConversionService for consistent behavior
 */
const classifyUnit = (unit: string | undefined): FieldType => {
	if (!unit?.trim()) return FieldType.UNKNOWN;

	const normalizedUnit = unit.trim();

	// Check currency first (special formatting needs)
	if (CURRENCY_UNITS.includes(normalizedUnit)) {
		return FieldType.CURRENCY;
	}

	// Use conversion service logic to determine field type
	if (canUnitBeConverted(unit)) {
		return FieldType.SYSTEM_OF_MEASUREMENT; // → UnitField (with conversions)
	} else {
		return FieldType.SCIENTIFIC_UNIT; // → ScientificUnitField (no conversions)
	}
};

/**
 * Check if a unit requires system of measurement field (current UnitField)
 */
export const isSystemOfMeasurementUnit = (
	unit: string | undefined,
): boolean => {
	return classifyUnit(unit) === FieldType.SYSTEM_OF_MEASUREMENT;
};

/**
 * Check if a unit requires scientific unit field
 */
export const isScientificUnit = (unit: string | undefined): boolean => {
	return classifyUnit(unit) === FieldType.SCIENTIFIC_UNIT;
};

/**
 * Check if a unit requires currency field
 */
export const isCurrencyUnit = (unit: string | undefined): boolean => {
	return classifyUnit(unit) === FieldType.CURRENCY;
};
